import { ComponentProps, LegacyRef, memo, useCallback } from 'react'
import { Link as RouterLink, LinkProps, useNavigate } from '@tanstack/react-router'
import cn from 'classnames'

import { IGeneralProps } from './types'

interface IProps extends Omit<IGeneralProps, 'to' | 'onClick'> {
  contentPosition?: 'contentStart' | 'contentCenter' | 'contentEnd' | 'contentSpaceBetween'
  disabled?: boolean
  hasPadding?: boolean
  innerRef?: LegacyRef<HTMLAnchorElement>
  onClick?: ComponentProps<typeof RouterLink>['onClick']
  size?: 'xs' | 'sm' | 'nm' | 'lg' | 'xl'
  testId?: string
  to?: LinkProps['to']
}

export const LinkButton = ({
  children,
  className,
  hasPadding,
  size = 'nm',
  contentPosition = 'contentCenter',
  innerRef,
  onClick,
  to,
  inverse,
  isShowTargetIcon,
  testId,
  ...props
}: IProps) => {
  const navigate = useNavigate()

  const handleClick = useCallback(
    (event: any) => {
      if (to) {
        navigate({ to })
      }

      if (onClick) {
        onClick(event)
      }
    },
    [to, onClick, navigate]
  )

  const fullProps = {
    ref: innerRef,
    'data-test-id': testId,
    className: cn(
      'cursor-pointer',
      {
        'flex flex-col items-center': hasPadding,
        'justify-start': hasPadding && contentPosition === 'contentStart',
        'justify-center': hasPadding && contentPosition === 'contentCenter',
        'justify-end': hasPadding && contentPosition === 'contentEnd',
        'justify-between': hasPadding && contentPosition === 'contentSpaceBetween',
        'h-[48px] min-w-[40px] px-5 py-0 text-body-lg': hasPadding && size === 'xl',
        'h-[40px] min-w-[40px] px-4 py-0 text-body-md': hasPadding && size === 'lg',
        'h-[32px] min-w-[32px] px-2 py-0 text-body-md': hasPadding && size === 'nm',
        'h-[28px] min-w-[28px] px-1 py-0 text-body-sm': hasPadding && size === 'sm',
        'h-[24px] px-1 py-0 text-body-sm': hasPadding && size === 'xs'
      },
      inverse ? 'link-inverse' : 'link',
      !isShowTargetIcon && 'after:hidden',
      'focus:z-10 focus:rounded-sm focus:border-primary focus:ring-md focus:ring-primary/focused',
      className
    ),
    ...props
  }

  return (
    <>
      {to ? (
        <RouterLink to={to} {...fullProps}>
          {children}
        </RouterLink>
      ) : (
        <a {...fullProps} onClick={handleClick}>
          {children}
        </a>
      )}
    </>
  )
}

export default memo(LinkButton)
